import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, ExternalLink, Map as MapIcon, Eye } from "lucide-react";

const PDOK = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free";

export const AddressMapButton = ({ address, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("kaart");
  const [ll, setLl] = useState(null);
  const q = (address || "").replace(/\s+/g, " ").replace(/^[\s,]+|[\s,]+$/g, "");

  useEffect(() => {
    if (!open || ll || !q) { return; }
    fetch(`${PDOK}?q=${encodeURIComponent(q)}&fq=type:adres&fl=centroide_ll&rows=1`)
      .then((r) => r.json())
      .then((d) => {
        const m = (d?.response?.docs?.[0]?.centroide_ll || "").match(/POINT\(([\d.]+) ([\d.]+)\)/);
        setLl(m ? { lng: m[1], lat: m[2] } : false);
      })
      .catch(() => setLl(false));
  }, [open, ll, q]);

  if (!q || q.length < 4) return null;
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(ll ? `${ll.lat},${ll.lng}` : q)}&z=17&output=embed`;
  const svSrc = ll ? `https://maps.google.com/maps?layer=c&cbll=${ll.lat},${ll.lng}&cbp=11,0,0,0,0&output=svembed` : "";
  const extUrl = view === "streetview" && ll
    ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${ll.lat},${ll.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

  return (
    <span onClick={(e) => e.stopPropagation()} className="inline">
      <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }} title="Toon op kaart / Street View"
        className={`inline-flex items-center justify-center h-6 w-6 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors align-middle ${className}`} data-testid="address-map-button">
        <MapPin className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="address-map-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2 pr-6"><MapPin className="h-5 w-5 text-blue-600" /> {q}</DialogTitle>
            <DialogDescription className="flex items-center justify-between gap-2 flex-wrap">
              <span className="inline-flex gap-1 bg-slate-100 p-1 rounded-lg">
                {[["kaart", "Kaart", MapIcon], ["streetview", "Street View", Eye]].map(([v, l, I]) => (
                  <button key={v} type="button" onClick={() => setView(v)} data-testid={`address-map-tab-${v}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm transition-colors ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                    <I className="h-3.5 w-3.5" /> {l}
                  </button>
                ))}
              </span>
              <a href={extUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline" data-testid="address-map-external">
                Openen in Google Maps <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </DialogDescription>
          </DialogHeader>
          <div className="h-[60vh] min-h-[320px] bg-slate-100 mt-3">
            {view === "kaart" && <iframe title="Kaart" src={mapSrc} className="w-full h-full border-0" loading="lazy" data-testid="address-map-iframe" />}
            {view === "streetview" && (ll
              ? <iframe title="Street View" src={svSrc} className="w-full h-full border-0" loading="lazy" allowFullScreen data-testid="address-streetview-iframe" />
              : <div className="h-full flex items-center justify-center text-sm text-slate-500 p-6 text-center">{ll === false ? "Locatie niet gevonden voor Street View. Gebruik 'Openen in Google Maps'." : "Locatie opzoeken…"}</div>)}
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
};
